import { centsToDollars, dollarsToCents } from "helpers/price";
import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useGetBillingCodesQuery } from "store/queries/billingCode";
import {
  useCreateDirectPayLinkMutation,
  useUpdateDirectPayLinkMutation,
} from "store/queries/directPayLinks";
import { useGetProvidersByAccountQuery } from "store/queries/providers";
import styled from "styled-components";
import { IBillingCode } from "types/BillingCode";
import { IDirectPayLink } from "types/DirectPayLink.d";

// Styled AutoComplete to handle long text overflow
const StyledAutoComplete = styled(AutoComplete)`
  .p-autocomplete-input {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;

interface IDirectPayLinkFormProps {
  accountId: string;
  editingLink?: IDirectPayLink | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface IFormData {
  cptCode: string;
  cptDescription: string;
  amount: number;
  amountEditable: boolean;
  showAccountNumber: boolean;
  showProviderName: boolean;
  providerIds: string[];
  header: string;
  subheader: string;
  maxOrders: number;
  expiresInDays: number;
}

export function DirectPayLinkForm({
  accountId,
  editingLink,
  onSuccess,
  onCancel,
}: IDirectPayLinkFormProps) {
  const toast = useRef<Toast>(null);
  const { data: billingCodesFromAPI } = useGetBillingCodesQuery();
  const billingCodes = billingCodesFromAPI?.data || [];

  // Fetch providers for this account
  const { data: providersData } = useGetProvidersByAccountQuery({
    accountId,
  });
  const accountProviders = providersData?.data || [];

  // Map providers to MultiSelect options
  const providerOptions = useMemo(
    () =>
      accountProviders.map((provider) => ({
        label: provider.name,
        value: provider.id,
      })),
    [accountProviders]
  );

  const [createLink, { isLoading: isCreating }] =
    useCreateDirectPayLinkMutation();
  const [updateLink, { isLoading: isUpdating }] =
    useUpdateDirectPayLinkMutation();

  const [selectedCptCode, setSelectedCptCode] = useState<string>("");
  const [filteredBillingCodes, setFilteredBillingCodes] = useState<
    IBillingCode[]
  >([]);

  // Calculate orders remaining and max possible orders
  const ordersCreated = editingLink?.ordersCreated || 0;
  const ordersRemaining = editingLink
    ? editingLink.maxOrders - editingLink.ordersCreated
    : 500;
  const maxPossibleOrders = ordersCreated + 500; // Can add up to 500 more orders

  // Calculate days until expiration for editing (always from now)
  const calculateDaysUntilExpiration = (expiresAt: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const expiration = new Date(expiresAt);
    expiration.setHours(0, 0, 0, 0);

    const diffTime = expiration.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, Math.min(300, diffDays)); // Between 1-300 days
  };

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IFormData>({
    defaultValues: {
      cptCode: editingLink?.cptCode || "G0463",
      cptDescription: editingLink?.cptDescription || "",
      amount:
        editingLink && editingLink.amountInCents != null
          ? centsToDollars(editingLink.amountInCents)
          : undefined,
      amountEditable: editingLink?.amountEditable ?? true,
      showAccountNumber: editingLink?.showAccountNumber ?? true,
      showProviderName: editingLink?.showProviderName ?? false,
      providerIds: editingLink?.providers?.map((p) => p.id) || [],
      header: editingLink?.header || "",
      subheader: editingLink?.subheader || "",
      maxOrders: editingLink?.maxOrders || 500,
      expiresInDays: editingLink
        ? calculateDaysUntilExpiration(editingLink.expiresAt)
        : 30,
    },
  });

  const { getFormErrorMessage } = useFormValidation();

  // Initialize selected CPT code for display
  useEffect(() => {
    if (editingLink) {
      const billingCode = billingCodes.find(
        (code) => code.name === editingLink.cptCode
      );
      if (billingCode) {
        setSelectedCptCode(`${billingCode.name} - ${billingCode.description}`);
      } else {
        setSelectedCptCode(
          `${editingLink.cptCode}${
            editingLink.cptDescription ? ` - ${editingLink.cptDescription}` : ""
          }`
        );
      }
    } else {
      // Set default G0463
      const defaultCode = billingCodes.find((code) => code.name === "G0463");
      if (defaultCode) {
        setSelectedCptCode(`${defaultCode.name} - ${defaultCode.description}`);
        setValue("cptDescription", defaultCode.description);
      }
    }
  }, [editingLink, billingCodes, setValue]);

  // Watch amount field and automatically check "Amount is editable" when amount is null
  const amountValue = watch("amount");
  const showProviderNameValue = watch("showProviderName");

  useEffect(() => {
    // If amount is null/empty/undefined, automatically set amountEditable to true
    // This ensures clients can enter amount when no preset amount is configured
    if (amountValue == null || amountValue === undefined) {
      setValue("amountEditable", true);
    }
  }, [amountValue, setValue]);

  // Helper function to truncate long descriptions
  const truncateDescription = (description: string, maxLength = 60) => {
    if (description.length <= maxLength) return description;
    return `${description.substring(0, maxLength)}...`;
  };

  const parsedFilteredBillingCodes = useMemo(() => {
    return filteredBillingCodes.map(
      (billingCode: IBillingCode) =>
        `${billingCode.name} - ${billingCode.description}`
    );
  }, [filteredBillingCodes]);

  // Template for displaying CPT codes in dropdown with truncated descriptions
  const cptCodeItemTemplate = (item: string) => {
    const parts = item.split(" - ");
    const code = parts[0];
    const description = parts.slice(1).join(" - ");
    const truncatedDesc = truncateDescription(description, 60);

    return (
      <div className="flex align-items-center" style={{ maxWidth: "100%" }}>
        <span className="font-semibold mr-2">{code}</span>
        <span className="text-sm text-gray-600" title={description}>
          {truncatedDesc}
        </span>
      </div>
    );
  };

  const searchBillingCode = (event: { query: string }) => {
    const query = event.query.toLowerCase().trim();

    // If query is empty (dropdown clicked), show top 50 most common codes
    if (query.length === 0) {
      setFilteredBillingCodes(billingCodes.slice(0, 50));
      return;
    }

    // Require minimum 2 characters to search (performance optimization)
    if (query.length < 2) {
      setFilteredBillingCodes([]);
      return;
    }

    // Filter billing codes
    const filtered = billingCodes.filter(
      (code) =>
        code.name.toLowerCase().includes(query) ||
        code.description.toLowerCase().includes(query)
    );

    // Limit results to prevent DOM overload (show max 50 results)
    const limitedResults = filtered.slice(0, 50);
    setFilteredBillingCodes(limitedResults);
  };

  const handleCptCodeSelect = (value: string) => {
    const codeMatch = value.match(/^([^\s-]+)/);
    if (codeMatch) {
      const code = codeMatch[1];
      const description = value.split(" - ").slice(1).join(" - ");
      setValue("cptCode", code);
      setValue("cptDescription", description);
    }
  };

  const onSubmit = async (data: IFormData) => {
    try {
      // Always calculate expiration from now (not from creation date)
      // Set to end of day (23:59:59) in UTC to avoid timezone issues
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + data.expiresInDays);
      expirationDate.setUTCHours(23, 59, 59, 999);

      // Validate orders remaining logic
      const newOrdersRemaining = data.maxOrders - ordersCreated;
      if (newOrdersRemaining > 500) {
        toast.current?.show({
          severity: "error",
          summary: "Validation Error",
          detail: "Orders remaining cannot exceed 500",
          life: 3000,
        });
        return;
      }

      const basePayload = {
        cptCode: data.cptCode,
        cptDescription: data.cptDescription,
        amountInCents:
          data.amount != null && data.amount > 0
            ? dollarsToCents(data.amount)
            : null,
        amountEditable: data.amountEditable,
        showAccountNumber: data.showAccountNumber,
        showProviderName: data.showProviderName,
        providerIds: data.showProviderName ? data.providerIds : [],
        header: data.header || null,
        subheader: data.subheader || null,
        maxOrders: data.maxOrders,
        expiresAt: expirationDate.toISOString(),
      };

      if (editingLink) {
        // When updating, don't send accountId (it cannot be changed)
        await updateLink({ id: editingLink.id, data: basePayload }).unwrap();
        toast.current?.show({
          severity: "success",
          summary: "Link Updated",
          detail: "Direct pay link has been updated successfully",
          life: 3000,
        });
      } else {
        // When creating, include accountId
        await createLink({ ...basePayload, accountId }).unwrap();
        toast.current?.show({
          severity: "success",
          summary: "Link Created",
          detail: "Direct pay link has been created successfully",
          life: 3000,
        });
      }
      onSuccess();
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to ${
          editingLink ? "update" : "create"
        } direct pay link`,
        life: 3000,
      });
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="formgrid grid">
          <div className="field col-12 md:col-6">
            <Label htmlFor="cptCode">CPT Code</Label>
            <StyledAutoComplete
              id="cptCode"
              value={selectedCptCode}
              suggestions={parsedFilteredBillingCodes}
              completeMethod={searchBillingCode}
              onChange={(e) => setSelectedCptCode(e.value)}
              onSelect={(e) => handleCptCodeSelect(e.value)}
              placeholder="Type at least 2 characters to search..."
              className="w-full"
              dropdown
              itemTemplate={cptCodeItemTemplate}
              minLength={2}
            />
            {errors.cptCode && (
              <small className="p-error">{errors.cptCode.message}</small>
            )}
            {parsedFilteredBillingCodes.length === 50 && (
              <small className="text-gray-500 text-xs">
                Showing first 50 results. Type more to narrow down.
              </small>
            )}
          </div>

          <div className="field col-12 md:col-6">
            <Label htmlFor="amount">Amount ($)</Label>
            <Controller
              name="amount"
              control={control}
              rules={{
                min: { value: 0.01, message: "Amount must be greater than 0" },
              }}
              render={({ field }) => (
                <InputNumber
                  id="amount"
                  value={field.value}
                  onValueChange={(e) => field.onChange(e.value)}
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  placeholder="Leave blank for client to enter"
                  className="w-full"
                />
              )}
            />
            {getFormErrorMessage("amount", errors)}
          </div>

          <div className="field col-12">
            <Label htmlFor="maxOrders">
              {editingLink ? "Total Orders Capacity" : "Max Orders"}
            </Label>
            {editingLink && (
              <div className="mb-2 text-sm text-gray-600">
                <div>Orders Created: {ordersCreated}</div>
                <div>Orders Remaining: {ordersRemaining}</div>
                <div className="text-blue-600">
                  You can add up to {500 - ordersRemaining} more orders
                </div>
              </div>
            )}
            <Controller
              name="maxOrders"
              control={control}
              rules={{
                required: "Max orders is required",
                min: {
                  value: editingLink ? ordersCreated : 1,
                  message: editingLink
                    ? `Cannot be less than orders already created (${ordersCreated})`
                    : "Must be at least 1",
                },
                max: {
                  value: editingLink ? maxPossibleOrders : 500,
                  message: editingLink
                    ? `Cannot exceed ${maxPossibleOrders} (${ordersCreated} created + 500 max remaining)`
                    : "Cannot exceed 500 orders",
                },
              }}
              render={({ field }) => (
                <InputNumber
                  id="maxOrders"
                  value={field.value}
                  onValueChange={(e) => field.onChange(e.value)}
                  placeholder={
                    editingLink
                      ? `Enter total capacity (${ordersCreated}-${maxPossibleOrders})`
                      : "Enter max orders (1-500)"
                  }
                  className="w-full"
                  min={editingLink ? ordersCreated : 1}
                  max={editingLink ? maxPossibleOrders : 500}
                />
              )}
            />
            {getFormErrorMessage("maxOrders", errors)}
          </div>

          <div className="field col-12">
            <Label htmlFor="expiresInDays">
              {editingLink
                ? "Extend Expiration (Days from now)"
                : "Expires In (Days)"}
            </Label>
            {editingLink && (
              <div className="mb-2 text-sm text-gray-600">
                Current expiration:{" "}
                {new Date(editingLink.expiresAt).toLocaleDateString()}
              </div>
            )}
            <Controller
              name="expiresInDays"
              control={control}
              rules={{
                required: "Expiration days is required",
                min: { value: 1, message: "Must be at least 1 day from now" },
                max: { value: 300, message: "Cannot exceed 300 days from now" },
              }}
              render={({ field }) => (
                <InputNumber
                  id="expiresInDays"
                  value={field.value}
                  onValueChange={(e) => field.onChange(e.value)}
                  placeholder="Enter number of days from now (1-300)"
                  className="w-full"
                  min={1}
                  max={300}
                />
              )}
            />
            {getFormErrorMessage("expiresInDays", errors)}
          </div>

          <div className="field col-12 md:col-6">
            <Controller
              name="amountEditable"
              control={control}
              render={({ field }) => {
                const isAmountEmpty =
                  amountValue == null || amountValue === undefined;
                return (
                  <div>
                    <div className="flex align-items-center">
                      <Checkbox
                        inputId="amountEditable"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.checked)}
                        disabled={isAmountEmpty}
                      />
                      <label
                        htmlFor="amountEditable"
                        className="ml-2 mb-0"
                        style={{
                          cursor: isAmountEmpty ? "default" : "pointer",
                        }}
                      >
                        Amount is editable in form
                      </label>
                    </div>
                    {isAmountEmpty && (
                      <small className="text-gray-500 ml-4">
                        (Required when amount is not set)
                      </small>
                    )}
                  </div>
                );
              }}
            />
          </div>

          <div className="field col-12 md:col-6">
            <Controller
              name="showAccountNumber"
              control={control}
              render={({ field }) => (
                <div className="flex align-items-center">
                  <Checkbox
                    inputId="showAccountNumber"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />
                  <label
                    htmlFor="showAccountNumber"
                    className="ml-2 mb-0"
                    style={{ cursor: "pointer" }}
                  >
                    Show Account Number field
                  </label>
                </div>
              )}
            />
          </div>

          <div className="field col-12 md:col-6">
            <Controller
              name="showProviderName"
              control={control}
              render={({ field }) => (
                <div className="flex align-items-center">
                  <Checkbox
                    inputId="showProviderName"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />
                  <label
                    htmlFor="showProviderName"
                    className="ml-2 mb-0"
                    style={{ cursor: "pointer" }}
                  >
                    Show Provider Selection field
                  </label>
                </div>
              )}
            />
          </div>

          {showProviderNameValue && providerOptions.length > 0 && (
            <div className="field col-12">
              <Label htmlFor="providerIds">
                Select Providers for Direct Pay Link{" "}
                <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="providerIds"
                control={control}
                rules={{
                  validate: (value) =>
                    !showProviderNameValue ||
                    (value && value.length > 0) ||
                    "Please select at least one provider",
                }}
                render={({ field }) => (
                  <MultiSelect
                    id="providerIds"
                    value={field.value?.filter((id: string) =>
                      providerOptions.some((opt) => opt.value === id)
                    )}
                    options={providerOptions}
                    onChange={(e) => field.onChange(e.value)}
                    placeholder="Select providers to show in the dropdown"
                    className="w-full"
                    display="chip"
                    filter
                    filterPlaceholder="Search providers..."
                    emptyFilterMessage="No providers found"
                  />
                )}
              />
              {getFormErrorMessage("providerIds", errors)}
              <small className="text-gray-500 mt-1 block">
                Patients will select from these providers.
              </small>
            </div>
          )}

          <div className="field col-12">
            <Label htmlFor="header">
              Header Text{" "}
              <span className="text-gray-500 font-normal text-sm">
                (Optional - default: &quot;Pay Now to Confirm Your
                Appointment&quot;)
              </span>
            </Label>
            <Controller
              name="header"
              control={control}
              render={({ field }) => (
                <InputText
                  id="header"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  className="w-full"
                  maxLength={100}
                />
              )}
            />
          </div>

          <div className="field col-12">
            <Label htmlFor="subheader">
              Subheader Text{" "}
              <span className="text-gray-500 font-normal text-sm">
                (Optional - default: &quot;Complete the form below to pay for
                your visit&quot;)
              </span>
            </Label>
            <Controller
              name="subheader"
              control={control}
              render={({ field }) => (
                <InputText
                  id="subheader"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  className="w-full"
                  maxLength={200}
                />
              )}
            />
          </div>
        </div>

        <div className="flex justify-content-end gap-2 mt-3">
          <Button
            type="button"
            label="Cancel"
            icon="pi pi-times"
            className="p-button-text"
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={editingLink ? "Update Link" : "Create Link"}
            icon="pi pi-check"
            loading={isCreating || isUpdating}
          />
        </div>
      </form>
    </>
  );
}
